import { ResearchSharing } from "./ResearchSharing";
import { ResearchRequests } from "./ResearchRequests";
import { InvestorServiceRequests } from "./InvestorServiceRequests";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Library, ClipboardList, Users } from "lucide-react";
import { researchAdvisingService } from "@/services/researchAdvisingService";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface ResearchHubProps {
  userRole: "investor" | "advisor" | "analyst";
  userId?: string;
}

export function ResearchHub({ userRole, userId }: ResearchHubProps) {
  const currentUserId = userId || localStorage.getItem('userId') || "user1";
  const currentUserRole = (userRole || localStorage.getItem('userRole') || "investor").toLowerCase();
  const [requests, setRequests] = useState<any[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);

  const fetchRequests = async () => {
    // Investors have their own fetching in InvestorServiceRequests
    if (currentUserRole === "investor") return;
    
    try {
      setIsLoadingRequests(true);
      const response = await researchAdvisingService.getRequests(currentUserId);
      const data = response.data || response;
      if (Array.isArray(data)) {
        setRequests(data);
      }
    } catch (error) {
      console.error("Error fetching requests in ResearchHub:", error);
      // Silent error or toast if needed
    } finally {
      setIsLoadingRequests(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [currentUserId, currentUserRole]);

  // Investors get a special combined view for both research and advising requests
  if (currentUserRole === "investor") {
    return (
      <div className="space-y-6">
        {/* Tabbed Interface for Investors */}
        <Tabs defaultValue="library" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="library" className="gap-2">
              <Library className="h-4 w-4" />
              Research Library
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-2">
              <ClipboardList className="h-4 w-4" />
              Service Requests
            </TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="space-y-6">
            <ResearchSharing userRole={currentUserRole as any} userId={currentUserId} />
          </TabsContent>

          <TabsContent value="requests" className="space-y-6">
            <InvestorServiceRequests />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Advisors and Analysts get the standard view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl">Research Center</h2>
        <p className="text-muted-foreground">
          {currentUserRole === "analyst"
            ? "Share research and manage client requests"
            : "Access research insights and request custom analysis"}
        </p>
      </div>

      {/* Tabbed Interface */}
      <Tabs defaultValue="library" className="space-y-6">
        <TabsList className={`grid w-full ${currentUserRole === "advisor" ? "grid-cols-3 max-w-2xl" : "grid-cols-2 max-w-md"}`}>
          <TabsTrigger value="library" className="gap-2">
            <Library className="h-4 w-4" />
            Research Library
          </TabsTrigger>
          <TabsTrigger value="requests" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            {currentUserRole === "analyst" ? "Client Requests" : "My Requests"}
          </TabsTrigger>
          {currentUserRole === "advisor" && (
            <TabsTrigger value="client-requests" className="gap-2">
              <Users className="h-4 w-4" />
              Client Requests
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="library" className="space-y-6">
          <ResearchSharing userRole={currentUserRole as any} userId={currentUserId} />
        </TabsContent>

        <TabsContent value="requests" className="space-y-6">
          <ResearchRequests 
            key={`${currentUserRole}-requests`}
            userRole={currentUserRole as any} 
            userId={currentUserId} 
            initialRequests={currentUserRole === "analyst" 
              ? requests.filter(r => r.recipientId == currentUserId)
              : requests.filter(r => r.requesterId == currentUserId)}
            isLoading={isLoadingRequests}
            onRefresh={fetchRequests}
          />
        </TabsContent>

        {currentUserRole === "advisor" && (
          <TabsContent value="client-requests" className="space-y-6">
            <ResearchRequests 
              key={`${currentUserRole}-client-requests`}
              userRole="analyst" // Advisor acts as provider (analyst role) for client requests
              userId={currentUserId} 
              initialRequests={requests.filter(r => 
                r.recipientId == currentUserId && 
                r.requesterRole?.toLowerCase() === "investor"
              )}
              isLoading={isLoadingRequests}
              onRefresh={fetchRequests}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
